import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import { loadHubspotFormsScript, waitForThen } from './helpers';

// HubSpot refuses API submissions for any form with captcha enabled
// (FORM_HAS_RECAPTCHA_ENABLED, on both the public and the authenticated endpoint), so a
// custom form can only reach it through HubSpot's own embed. This renders that embed
// off-screen and drives it: our UI collects the values, the embed carries the captcha.

const SUBMIT_TIMEOUT = 15000;
const GENERIC_ERROR = 'Oops! Something went wrong while submitting the form.';
const UNAVAILABLE_ERROR = 'Sorry, the sign up form is unavailable right now. Please try again shortly.';

// The embed is React and builds its submission from its own state, not from the DOM.
// React installs a value setter on the element that swallows a plain assignment, so the
// change has to go through the prototype's setter for the input event to register.
const setFieldValue = (field, value) => {
  const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(field), 'value');
  if (descriptor && descriptor.set) {
    descriptor.set.call(field, value);
  } else {
    // eslint-disable-next-line no-param-reassign
    field.value = value;
  }

  // blur is what triggers HubSpot's own email validation.
  ['input', 'change', 'blur'].forEach((eventName) => {
    field.dispatchEvent(new Event(eventName, { bubbles: true }));
  });
};

// v2 hands callbacks a jQuery object. A real <form> also has a truthy length (its
// controls) and form[0] is the first control, so check for a DOM node first.
const toElement = (form) => {
  if (!form) return null;
  if (form.nodeType === 1) return form;
  return form.length ? form[0] : null;
};

// Nothing in the off-screen embed should be reachable by keyboard or announced.
const removeFromTabOrder = (form) => {
  form.querySelectorAll('a, button, input, select, textarea, iframe').forEach((element) => {
    element.setAttribute('tabindex', '-1');
  });
};

// Consent lives in the HubSpot form definition; the visible UI carries the matching copy,
// as the legalConsentOptions in the old API payload did. Ticking these is what makes the
// submission carry consentToProcess and the newsletter subscription.
const acceptConsent = (form) => {
  const subscriptionId = process.env.GATSBY_HUBSPOT_NEWSLETTER_SUBSCRIPTION_ID;
  // Deliberately only these two: processing consent, which the visible copy covers, and
  // the newsletter subscription the user just signed up for. Any other consent box is an
  // opt-in the visible form never offered, so it is left alone.
  const selectors = [
    'input[name="LEGAL_CONSENT.processing"]',
    subscriptionId ? `input[name="LEGAL_CONSENT.subscription_type_${subscriptionId}"]` : null,
  ].filter(Boolean);

  // A Set because the selectors overlap, and clicking a box twice would untick it.
  const checkboxes = new Set();
  selectors.forEach((selector) => {
    form.querySelectorAll(selector).forEach((checkbox) => checkboxes.add(checkbox));
  });

  checkboxes.forEach((checkbox) => {
    if (!checkbox.checked) checkbox.click();
  });
};

const readError = (form) => {
  const message = form.querySelector('.hs-error-msgs li, .hs_error_rollup li');
  return message ? message.textContent.trim() : null;
};

const useHubspotProxyForm = ({ formId, timeout = SUBMIT_TIMEOUT }) => {
  const containerRef = useRef(null);
  const formRef = useRef(null);
  const mountedRef = useRef(true);
  const timeoutRef = useRef(null);
  const observerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const stopWatching = useCallback(() => {
    clearTimeout(timeoutRef.current);
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  const succeed = useCallback(() => {
    stopWatching();
    if (!mountedRef.current) return;
    setSubmitting(false);
    setSubmitted(true);
  }, [stopWatching]);

  const fail = useCallback((message) => {
    stopWatching();
    if (!mountedRef.current) return;
    setSubmitting(false);
    setError(message);
  }, [stopWatching]);

  const targetId = `hubspotProxyForm-${formId}`;

  useEffect(() => {
    mountedRef.current = true;
    if (typeof window === 'undefined' || !formId) return undefined;

    // Fires instead of the callback when HubSpot renders the form in an iframe.
    const handleMessage = ({ data }) => {
      if (!data || data.type !== 'hsFormCallback' || data.id !== formId) return;
      if (data.eventName === 'onFormSubmitted') succeed();
    };
    window.addEventListener('message', handleMessage);

    loadHubspotFormsScript();
    waitForThen(() => window.hbspt !== undefined, () => {
      if (!mountedRef.current) return;
      window.hbspt.forms.create({
        region: process.env.GATSBY_HUBSPOT_REGION,
        portalId: process.env.GATSBY_HUBSPOT_ACCOUNT_ID,
        formId,
        target: `#${targetId}`,
        onFormReady: (form) => {
          formRef.current = toElement(form);
          if (formRef.current) removeFromTabOrder(formRef.current);
          if (mountedRef.current) setReady(true);
        },
        onFormSubmitted: succeed,
      });
    });

    return () => {
      mountedRef.current = false;
      window.removeEventListener('message', handleMessage);
      stopWatching();
    };
  }, [formId, succeed, stopWatching, targetId]);

  const submit = useCallback((values) => {
    const form = formRef.current
      || (containerRef.current && containerRef.current.querySelector('form'));

    if (!form) {
      fail(UNAVAILABLE_ERROR);
      return;
    }

    // A previous attempt may still be armed; never leave a stray observer or timer behind.
    stopWatching();
    setError(null);
    setSubmitting(true);

    const unmapped = Object.entries(values).filter(([name, value]) => {
      const field = form.querySelector(`[name="${name}"]`);
      if (!field) return true;
      setFieldValue(field, value);
      return false;
    });

    // The HubSpot form no longer has a field we submit — submitting would drop the value.
    if (unmapped.length) {
      fail(UNAVAILABLE_ERROR);
      return;
    }

    acceptConsent(form);

    // A stale message from a previous attempt would be read back as this attempt's error.
    form.querySelectorAll('.hs-error-msgs').forEach((node) => node.remove());

    // Armed before submitting: HubSpot can call back synchronously, and succeed() has to
    // be able to clear these. Rejections (captcha, validation) render inside the
    // off-screen form and fire no callback, so the only signal is the error node appearing.
    observerRef.current = new MutationObserver(() => {
      const message = readError(form);
      if (message) fail(message);
    });
    observerRef.current.observe(form, { childList: true, subtree: true, characterData: true });
    timeoutRef.current = setTimeout(() => fail(GENERIC_ERROR), timeout);

    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    if (submitButton) {
      submitButton.click();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }, [fail, stopWatching, timeout]);

  return {
    containerRef, targetId, ready, submitting, submitted, error, submit,
  };
};

export default useHubspotProxyForm;
